<template>
  <div class="page-container">
    <RttHeader v-if="!$rttI18n.$isWidget" />
    <AlertComponent />
    <div class="postcode-page">
      <UpdateCard
        v-if="
          trackingDataStore.isJobInProgress &&
          !websocketStore.isSubscriptionActive
        "
      />
      <BigBen />
      <PostcodeInputCard :trackingId="props.trackingId" />
      <div
        :class="{ gray: trackingDataStore.isJobDeliveryStatusPreEta }"
        class="static-map background-map"
      />
    </div>
    <RttFooter v-if="!$rttI18n.$isWidget" />
  </div>
  <SeasonalAnimationOverlay />
</template>

<script setup>
import PostcodeInputCard from '@/v2/components/cards/PostcodeInputCard.vue';
import UpdateCard from '@/v2/components/cards/UpdateCard.vue';
import RttFooter from '@/v2/components/layout/RttFooter.vue';
import RttHeader from '@/v2/components/layout/RttHeader.vue';
import AlertComponent from '@/v2/components/common/AlertComponent.vue';
import { useTrackingDataStore } from '@/stores/trackingData.js';
import { getUtmSource } from '@/router/queryParam.js';
import { inject } from 'vue';
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
import BigBen from '@/v2/components/tracking/BigBen.vue';
import { ERROR_ID_UNKNOWN_TRACKING_ID } from '@/stores/constants.js';
import { inputToTenant } from '@/config/countriesAndLocales.js';
import SeasonalAnimationOverlay from '@/v2/components/tracking/SeasonalAnimationOverlay.vue';
import { useDataRefreshOnVisibilityChange } from '@/v2/composables/useDataRefreshOnVisibilityChange.js';
import { useWebsocketStore } from '@/stores/websocket.js';

const props = defineProps({
  trackingId: {
    type: String,
    required: true
  }
});

const route = useRoute();
const router = useRouter();
const $rttI18n = inject('rttI18n');

const trackingDataStore = useTrackingDataStore();
const websocketStore = useWebsocketStore();

useDataRefreshOnVisibilityChange('postcode_page');

onBeforeRouteUpdate(async (to, from) => {
  const tenantId = inputToTenant(to.params.countryOrTenantId);

  if (
    to.params.trackingId !== from.params.trackingId ||
    to.params.countryOrTenantId !== from.params.countryOrTenantId
  ) {
    await trackingDataStore.loadTrackingDataById({
      utmSource: getUtmSource(route),
      tenantId,
      trackingId: to.params.trackingId
    });

    if (
      trackingDataStore.requestState === 'ERROR' &&
      trackingDataStore.errorPayload?.id === ERROR_ID_UNKNOWN_TRACKING_ID
    ) {
      trackingDataStore.resetTrackingData();
      await router.replace({
        name: 'previewLocaleLandingContainer',
        params: {
          countryOrTenantId: tenantId,
          locale: $rttI18n.locale,
          trackingId: props.trackingId
        }
      });
    }
  }
});
</script>

<style scoped>
.postcode-page {
  flex: 1;
  gap: 16px;
  padding: var(--pad-space-sp-40) var(--pad-space-sp-16);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  & :deep(.card-wrapper) {
    margin-left: 0 !important;
  }
}

@media (width < 1024px) {
  .postcode-page {
    padding: var(--page-mobile-card-padding);
  }
}

.static-map {
  background-image: url('/images/mock_map.svg');
  background-size: cover;
  background-position-x: left;
  background-position-y: top;
  background-repeat: no-repeat;

  &.gray {
    background-image: url('/images/mock_map_gray.svg');
  }
}
</style>
